import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { ConfigProvider, DatePicker, Select } from "antd";
import CommonSelect from "../../../../../core/common/commonSelect";

// Helpers
import api from "../../../../../services/api";
import { notify } from "../../../../../services/toast.config";
import {
  validateReportTypeForm,
  updateFieldValidationReportTypeForm,
  getDepartmentOptions,
  getEmployeeOptions,
  getDesignationOptions,
} from "../helpers/form.handler";

// TYPES
import { FrequencyType, FormData, ADD_REPORT_TYPE_PROPS } from "../types";
import Loader from "../../../../../core/common/loader";
import dayjs from "dayjs";

const FREQUENCY_TYPES: { value: FrequencyType; label: string }[] = [
  { value: "daily", label: "Every Day (select weekdays)" },
  { value: "weekly", label: "Weekly (reports per week)" },
  { value: "monthly", label: "Monthly (dates or reports per month)" },
  { value: "never", label: "Never" },
];

const WEEK_DAYS: { value: number; label: string }[] = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

// Updated FormData type to handle multiple selections
const DEFAULT_FORM_DATA: FormData = {
  company_id: null,
  company_type: "",
  name: "",
  frequencyType: "daily",
  days: [1, 2, 3, 4, 5, 6],
  weeklyInterval: "1",
  monthlyInterval: "1",
  startDate: null,
  endDate: null,
  description: "",
  department: [], // Array of selected department keys
  showsTo: [], // Array of selected employee keys
  designation: [], // Array of selected designation keys
  status: "",
};

const AddReportTypeModal: React.FC<ADD_REPORT_TYPE_PROPS> = ({
  employees,
  departments,
  designations,
  updateUI,
  selectedCompanyOption,
}) => {
  // Initialization
  const signature: string | null = localStorage.getItem("signature");
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  // STATES
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // HANDLERS
  const updateFieldValidation = useCallback(
    updateFieldValidationReportTypeForm,
    []
  );

  const onNumberInputChange = (field: keyof FormData, value: string) => {
    if (value === "" || /^[0-9]*$/.test(value)) {
      const newFormData = { ...formData, [field]: value };
      setFormData(newFormData);
      if (touchedFields.has(field)) {
        updateFieldValidation(field, newFormData, setValidationErrors);
      }
    }
  };

  const onChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    if (touchedFields.has(field)) {
      updateFieldValidation(field, newFormData, setValidationErrors);
    }
  };

  // Handle field blur (when user moves out of field)
  const handleFieldBlur = (field: string) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    updateFieldValidation(field, formData, setValidationErrors);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Mark all fields as touched on submit
    const allFields = [
      "name",
      "days",
      "weeklyInterval",
      "monthlyInterval",
      "status",
    ];
    setTouchedFields(new Set(allFields));

    if (!validateReportTypeForm(formData, setValidationErrors)) return;
    setIsSubmitting(true);
    setIsLoading(true);
    try {
      const companyId = selectedCompanyOption?.company_id;
      const companyType = selectedCompanyOption?.company_type;
      if (!companyId || !companyType) {
        throw new Error("Internal Service Error");
      }

      const dataToSend = {
        ...formData,
        signature: signature,
        company_id: parseInt(companyId),
        company_type: companyType,
        status: formData.status == "Active" ? 1 : 0,
      };
      const response = await api.post(
        "/ers/report-types/create-type",
        dataToSend
      );

      // Close modal
      const dismissButton = document.querySelector(
        "#add-report-type-dismiss-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }

      notify.success(response?.data?.message);

      setFormData(DEFAULT_FORM_DATA);
      setValidationErrors([]);
      setTouchedFields(new Set());
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage || "Internal Server Error!");
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
      updateUI();
    }
  };

  const handleCancleButton = () => {
    setFormData(DEFAULT_FORM_DATA);
    setValidationErrors([]);
    setTouchedFields(new Set());
  };

  // USE EFFECTS
  useEffect(() => {
    setValidationErrors([]);
    setTouchedFields(new Set());
    setFormData((prev: any) => ({
      ...DEFAULT_FORM_DATA,
      days: prev?.days,
      frequencyType: prev?.frequencyType,
      name: prev?.name,
    }));
  }, [formData.frequencyType]);

  useEffect(() => {
    setValidationErrors([]);
    setTouchedFields(new Set());
    setFormData(DEFAULT_FORM_DATA);
  }, []);

  // RENDER COMPONENT
  return (
    <>
      {isLoading && <Loader />}

      <div
        className="modal fade"
        id="ers_create_report_type_modal"
        tabIndex={-1}
        aria-labelledby="createReportTypeModalLabel"
        aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="createReportTypeModalLabel">
                Create Report Type
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {selectedCompanyOption?.company_id != "" ? (
                <div
                  className="modal-body px-4"
                  style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <div className="row mb-2">
                    {/* Report Name */}
                    <div className="col-md-6 mb-2">
                      <label htmlFor="reportName" className="form-label">
                        Report Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="reportName"
                        type="text"
                        className={`form-control text-muted${
                          validationErrors.includes("Report name is required.")
                            ? " is-invalid"
                            : ""
                        }`}
                        placeholder="Enter report type name, e.g. Daily Work Report"
                        value={formData.name}
                        onChange={(e) => onChange("name", e.target.value)}
                        onBlur={() => handleFieldBlur("name")}
                      />
                      {validationErrors.includes(
                        "Report name is required."
                      ) && (
                        <div className="invalid-feedback">
                          Report name is required.
                        </div>
                      )}
                    </div>

                    {/* Frequency */}
                    <div className="col-md-6 mb-2">
                      <label htmlFor="frequencyType" className="form-label">
                        Repeat Frequency <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className="form small"
                        options={FREQUENCY_TYPES}
                        placeHolder="Select frequency"
                        onChange={(e: any) =>
                          onChange("frequencyType", e?.value)
                        }
                        defaultValue={FREQUENCY_TYPES.find(
                          (f) => f.value === formData.frequencyType
                        )}
                      />
                    </div>
                  </div>

                  {/* Daily: Select Days */}
                  {formData.frequencyType === "daily" && (
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Select Days</label>
                        <div className="d-flex gap-3 flex-wrap mb-1">
                          {WEEK_DAYS.map((day) => (
                            <div
                              key={day.value}
                              className="form-check d-flex align-items-center gap-1 justify-content-center">
                              <input
                                className="form-check-input mb-1"
                                type="checkbox"
                                id={`dayCheckbox-${day.value}`}
                                checked={formData.days.includes(day.value)}
                                onChange={(e) => {
                                  let updated: number[];
                                  if (e.target.checked) {
                                    updated = [...formData.days, day.value];
                                  } else {
                                    updated = formData.days.filter(
                                      (d) => d !== day.value
                                    );
                                  }
                                  onChange("days", updated);
                                }}
                                onBlur={() => handleFieldBlur("days")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`dayCheckbox-${day.value}`}>
                                {day.label}
                              </label>
                            </div>
                          ))}
                        </div>
                        {validationErrors.includes(
                          "Select at least one day for daily/weekly frequency."
                        ) ? (
                          <div className="text-danger mt-1 small">
                            Select at least one day for daily/weekly frequency.
                          </div>
                        ) : (
                          <small className="text-success">
                            Select which days of the week the report repeats.
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Weekly */}
                  {formData.frequencyType === "weekly" && (
                    <div className="row align-items-start">
                      {/* Select working days  */}
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Select Working Days
                        </label>
                        <div className="d-flex gap-2 flex-wrap">
                          {WEEK_DAYS.map((day) => (
                            <div
                              key={day.value}
                              className="form-check d-flex align-items-center gap-1 justify-content-center">
                              <input
                                className="form-check-input mb-1"
                                type="checkbox"
                                id={`weekDayCheckbox-${day.value}`}
                                checked={formData.days.includes(day.value)}
                                onChange={(e) => {
                                  let updated: number[];
                                  if (e.target.checked) {
                                    updated = [...formData.days, day.value];
                                  } else {
                                    updated = formData.days.filter(
                                      (d) => d !== day.value
                                    );
                                  }
                                  onChange("days", updated);
                                }}
                                onBlur={() => handleFieldBlur("days")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`weekDayCheckbox-${day.value}`}>
                                {day.label}
                              </label>
                            </div>
                          ))}
                        </div>

                        {validationErrors.includes(
                          "Select at least one day for daily/weekly frequency."
                        ) ? (
                          <div className="text-danger mt-1 small">
                            Select at least one day.
                          </div>
                        ) : (
                          <small className="text-success">
                            Select the days for the weekly report.
                          </small>
                        )}
                      </div>

                      {/* Repeat Every Weeks  */}
                      <div className="col-md-6 mb-3">
                        <label htmlFor="weeklyInterval" className="form-label">
                          Repeat Every (Weeks)
                        </label>
                        <input
                          id="weeklyInterval"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={2}
                          className={`form-control${
                            validationErrors.some((err) =>
                              err.includes("Repeat interval")
                            )
                              ? " is-invalid"
                              : ""
                          }`}
                          value={formData.weeklyInterval}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            onNumberInputChange(
                              "weeklyInterval",
                              e.target.value
                            )
                          }
                          onBlur={() => handleFieldBlur("weeklyInterval")}
                        />
                        {validationErrors.some((err) =>
                          err.includes("Repeat interval")
                        ) ? (
                          <div className="invalid-feedback">
                            Repeat interval must be between 1 and 52.
                          </div>
                        ) : (
                          <small className="text-success">
                            Repeat every X weeks (1 = 1 report every week, 2 = 1
                            report every 2 weeks, 3 = 1 report every 3 weeks)
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Monthly */}
                  {formData.frequencyType === "monthly" && (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Select Working Days
                        </label>
                        <div className="d-flex gap-2 flex-wrap">
                          {WEEK_DAYS.map((day) => (
                            <div
                              key={day.value}
                              className="form-check d-flex align-items-center gap-1 justify-content-center">
                              <input
                                className="form-check-input mb-1"
                                type="checkbox"
                                id={`monthDayCheckbox-${day.value}`}
                                checked={formData.days.includes(day.value)}
                                onChange={(e) => {
                                  let updated: number[];
                                  if (e.target.checked) {
                                    updated = [...formData.days, day.value];
                                  } else {
                                    updated = formData.days.filter(
                                      (d) => d !== day.value
                                    );
                                  }
                                  onChange("days", updated);
                                }}
                                onBlur={() => handleFieldBlur("days")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`monthDayCheckbox-${day.value}`}>
                                {day.label}
                              </label>
                            </div>
                          ))}
                        </div>

                        {validationErrors.includes(
                          "Select at least one day for daily/weekly frequency."
                        ) ? (
                          <div className="text-danger mt-1 small">
                            Select at least one day.
                          </div>
                        ) : (
                          <small className="text-success">
                            Select the days for the weekly report.
                          </small>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="monthlyInterval" className="form-label">
                          Repeat Every (Months)
                        </label>
                        <input
                          id="monthlyInterval"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={2}
                          className={`form-control${
                            validationErrors.some((err) =>
                              err.includes("Repeat interval")
                            )
                              ? " is-invalid"
                              : ""
                          }`}
                          value={formData.monthlyInterval}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            onNumberInputChange(
                              "monthlyInterval",
                              e.target.value
                            )
                          }
                          onBlur={() => handleFieldBlur("monthlyInterval")}
                        />
                        {validationErrors.some((err) =>
                          err.includes("Repeat interval")
                        ) ? (
                          <div className="invalid-feedback">
                            Repeat interval must be between 1 and 12.
                          </div>
                        ) : (
                          <small className="text-success">
                            Repeat every X months (1 = 1 report every month, 2 =
                            one report every 2 months)
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Department | Shows To | Designation - Multi-select with Tags */}
                  <div className="row">
                    <ConfigProvider
                      theme={{
                        components: {
                          Select: {
                            multipleItemHeight: 24,
                            multipleItemBg: "#4c1d95",
                            multipleItemBorderColor: "#7c3aed",
                            colorText: "#ede9fe",
                            zIndexPopup: 1060, // Bootstrap modal is 1050, backdrop is 1040
                          },
                        },
                      }}>
                      {/* Department Multi-Select */}
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Department</label>
                        <Select
                          mode="multiple"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{
                            width: "100%",
                            maxHeight: "38px",
                            overflowX: "auto",
                            overflowY: "hidden",
                          }}
                          placeholder="Type to search departments..."
                          value={formData.department}
                          onChange={(selectedKeys) => {
                            onChange("department", selectedKeys);
                          }}
                          options={getDepartmentOptions(departments)}
                          maxTagCount="responsive"
                        />
                        <small className="text-muted">
                          Select report's department(s)
                        </small>
                      </div>

                      {/* Shows To Multi-Select */}
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Shows To</label>

                        <Select
                          mode="multiple"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{
                            width: "100%",
                            maxHeight: "38px",
                            overflowX: "auto",
                            overflowY: "hidden",
                          }}
                          placeholder="Type to search employees..."
                          value={formData.showsTo}
                          onChange={(selectedKeys) => {
                            onChange("showsTo", selectedKeys);
                          }}
                          options={getEmployeeOptions(employees)}
                          maxTagCount="responsive"
                          optionRender={(option) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "0",
                              }}>
                              <img
                                src={
                                  option.data.image_url ||
                                  "assets/img/users/user.png"
                                }
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}>
                                {option.label}
                              </span>
                            </div>
                          )}
                        />
                        <small className="text-muted">
                          Select who can view reports other than reporting
                          officer
                        </small>
                      </div>

                      {/* Designation Multi-Select */}
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Designation</label>
                        <Select
                          mode="multiple"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{
                            width: "100%",
                            maxHeight: "38px",
                            overflowX: "auto",
                            overflowY: "hidden",
                          }}
                          placeholder="Type to search designations..."
                          value={formData.designation}
                          onChange={(selectedKeys) => {
                            onChange("designation", selectedKeys);
                          }}
                          options={getDesignationOptions(
                            designations,
                            formData.department,
                            departments
                          )}
                          maxTagCount="responsive"
                        />
                        <small className="text-muted">
                          Select applicable designation(s)
                        </small>
                      </div>
                    </ConfigProvider>
                  </div>

                  {/* Status & Dates */}
                  <div className="row">
                    {/* Status  */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className="form small"
                        options={STATUS_OPTIONS}
                        placeHolder="Select Status"
                        onChange={(e: any) =>
                          onChange("status", e?.value ?? "")
                        }
                        defaultValue={STATUS_OPTIONS.find(
                          (s) => s.value === formData.status
                        )}
                      />
                      {validationErrors.includes("Status is required.") && (
                        <div className="text-danger small">
                          Status is required.
                        </div>
                      )}
                    </div>

                    {/* Start Date  */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Start Date <span className="text-danger">*</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control"
                          onChange={(date) =>
                            onChange("startDate", dayjs(date))
                          }
                          value={formData.startDate}
                          placeholder="Select start date"
                          allowClear={true}
                          onBlur={() => handleFieldBlur("startDate")}
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                      {validationErrors.includes("Start Date is required.") && (
                        <div className="text-danger small">
                          Start Date is required.
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        End Date <span className="text-muted">(optional)</span>
                      </label>
                      <div className="input-icon-end position-relative">
                        <DatePicker
                          className="form-control"
                          onChange={(date) => onChange("endDate", dayjs(date))}
                          value={formData.endDate}
                          placeholder="Select end date"
                        />
                        <span className="input-icon-addon">
                          <i className="ti ti-calendar text-gray-7" />
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-md-12 mb-1">
                    <label className="form-label">
                      Description <span className="text-muted">(optional)</span>
                    </label>
                    <textarea
                      className="form-control text-muted"
                      placeholder="Add a description"
                      rows={2}
                      value={formData.description}
                      onChange={(e) => onChange("description", e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className="modal-body px-4">
                  <div className="flex flex-col ">
                    <div className="text-center py-4">
                      <i className="ti ti-exclamation-circle text-danger fs-40"></i>
                      <h5 className="mt-3 text-danger">
                        <span className="text-danger">Oops!</span> No Company
                        Selected!
                      </h5>
                    </div>

                    <div className="alert alert-info d-flex aligin-items-center justify-content-center">
                      <i className="ti ti-info-circle me-2"></i>
                      Select company first from the main page.
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-md btn-danger me-3"
                  data-bs-dismiss="modal"
                  ref={submitBtnRef}
                  disabled={isSubmitting}
                  onClick={handleCancleButton}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-md btn-info"
                  disabled={
                    isSubmitting || selectedCompanyOption?.company_id == ""
                      ? true
                      : false
                  }>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </button>
                {/* Hidden button for programmatic modal close */}
                <button
                  id="add-report-type-dismiss-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddReportTypeModal;
